import {
  addDays,
  addMonths,
  differenceInMonths,
  eachDayOfInterval,
  endOfDay,
  format,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { withUnistyles } from 'react-native-unistyles';

import { Box, Button, VStack } from '@squire/ui';

import { ChevronLeftIcon } from '@/components/_icons/ChevronLeftIcon';
import { Calendar } from '@/components/_shared/Calendar/Calendar';
import { ChooseTimeCalendarDay } from '@/components/booking/ChooseTimeCalendarDay';
import { getToday } from '@/utils/dateUtils';
import { AVAILABILITY_WAITLIST_ONLY } from '@/utils/timeSlotUtils';

import { styles } from './styles';
import type { ChooseTimeCalendarProps } from './types';

const AnimatedView = withUnistyles(Animated.View);

// Smooth animations for calendar transitions
const calendarEnteringAnimation = FadeIn.duration(150);
const calendarExitingAnimation = FadeOut.duration(100);

export function ChooseTimeCalendar(props: ChooseTimeCalendarProps) {
  const {
    selectedDate,
    currentMonth: _currentMonth,
    availabilityMap,
    end,
    rangeEnd,
    monthsAhead,
    today,
    barberOffDates = [],
    isGroupAppointment = false,
    onDateSelect,
    onMonthChange,
    isLoading = false,
    testID = 'choose-time-calendar',
  } = props;

  const [calendarVisible, setCalendarVisible] = useState(false);
  const [localSelectedDate, setLocalSelectedDate] = useState(selectedDate);

  // "Today" anchored to the shop's calendar day (falls back to device today).
  // Frozen on mount so the earliest selectable day is stable for the session.
  const minDate = useRef(today ?? getToday()).current;
  // Latest month the calendar can page to: the end of the barber's
  // advance-booking window (computed in useReservationInterval).
  const maxDate = rangeEnd;

  useEffect(() => {
    setLocalSelectedDate((p) => {
      if (
        !isWithinInterval(selectedDate, {
          start: startOfDay(p),
          end: addDays(endOfDay(p), 5),
        })
      ) {
        return selectedDate;
      }
      return p;
    });
  }, [selectedDate]);

  const weekDays = useMemo(() => {
    const date =
      typeof localSelectedDate === 'string'
        ? parseISO(localSelectedDate)
        : localSelectedDate;
    return eachDayOfInterval({ start: date, end: addDays(date, 5) });
  }, [localSelectedDate]);

  const getDateKey = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  const barberOffDateKeys = useMemo(
    () => new Set(barberOffDates.map((date) => getDateKey(date))),
    [barberOffDates],
  );

  const isBarberOffDay = (date: Date): boolean =>
    barberOffDateKeys.has(getDateKey(date));

  const isDateDisabled = (date: Date): boolean => {
    if (date > end) return true;
    // A single appointment's day off fully disables the day. A group
    // appointment stays selectable — other guests may still be bookable that
    // day — and is shown with the existing waitlist-only styling instead
    // (see isWaitlistOnly below), which is already gray and already doesn't
    // disable press.
    if (!isGroupAppointment && isBarberOffDay(date)) return true;
    const dateKey = getDateKey(date);
    const availability = availabilityMap.get(dateKey);
    // While availability data is loading, treat unknown dates as potentially
    // available so the calendar is interactive. After loading, undefined means
    // the API returned no data for this date (outside range or barber is off).
    if (availability === undefined) return !isLoading;
    // Waitlist-only days (-1) are not disabled
    return availability === 0;
  };

  const isWaitlistOnly = (date: Date): boolean => {
    const dateKey = getDateKey(date);
    if (availabilityMap.get(dateKey) === AVAILABILITY_WAITLIST_ONLY) {
      return true;
    }
    return isGroupAppointment && isBarberOffDay(date);
  };

  const handleShowCalendar = () => {
    setCalendarVisible(true);
  };

  const handleCalendarDateSelect = (date: Date) => {
    setLocalSelectedDate(date);
    setCalendarVisible(false);
    onDateSelect(date);
  };

  // Generate disabled dates for the calendar picker
  // A date is disabled if:
  // 1. It has availability === 0 in the map (confirmed no slots), OR
  // 2. It's NOT in the availabilityMap AND loading is complete (outside range or barber is off), OR
  // 3. It's beyond the booking window
  const disabledDates = useMemo(() => {
    const disabled: Date[] = [];

    // Generate all dates from minDate to maxDate
    const allDates = eachDayOfInterval({ start: minDate, end: maxDate });

    for (const date of allDates) {
      const dateKey = getDateKey(date);
      const availability = availabilityMap.get(dateKey);

      // Disable if beyond booking window
      if (date > end) {
        disabled.push(date);
        continue;
      }

      // A single appointment's day off fully disables the day (see
      // isDateDisabled above for the group-appointment exception).
      if (!isGroupAppointment && barberOffDateKeys.has(dateKey)) {
        disabled.push(date);
        continue;
      }

      if (availability === undefined) {
        // While loading, skip unknown dates so the full-month calendar isn't
        // entirely greyed out. After loading, undefined means no data = disabled.
        if (!isLoading) disabled.push(date);
        continue;
      }

      if (availability === 0) {
        disabled.push(date);
      }
    }

    return disabled;
  }, [
    availabilityMap,
    minDate,
    maxDate,
    end,
    isLoading,
    isGroupAppointment,
    barberOffDateKeys,
  ]);

  // Calculate calendar range: today's month + `monthsAhead` following months
  // (driven by the barber's advance-booking window). Open at the selected
  // date's month.
  const {
    calendarCurrentMonth,
    monthsBehind,
    monthsAhead: calendarMonthsAhead,
  } = useMemo(() => {
    const todaysMonth = startOfMonth(minDate);
    const selectedMonth = startOfMonth(selectedDate);

    // How many months the selected date is ahead of today's month, clamped to
    // the available window [0, monthsAhead].
    const monthsFromToday = Math.max(
      0,
      Math.min(monthsAhead, differenceInMonths(selectedMonth, todaysMonth)),
    );

    // monthsBehind: months before the selected month to show (back to today's month)
    // monthsAhead: months after the selected month to show (up to the window end)
    return {
      calendarCurrentMonth: addMonths(todaysMonth, monthsFromToday),
      monthsBehind: monthsFromToday,
      monthsAhead: monthsAhead - monthsFromToday,
    };
  }, [selectedDate, monthsAhead, minDate]);

  const waitlistOnlyDates = useMemo(() => {
    const dates = Array.from(availabilityMap.entries()).reduce(
      (acc, [dateKey, availability]) => {
        if (availability === AVAILABILITY_WAITLIST_ONLY)
          acc.push(parseISO(dateKey));
        return acc;
      },
      [] as Date[],
    );

    // A group appointment's barber-off days get the same waitlist-only
    // styling (gray, still pressable) rather than the harder disabled state —
    // other guests may still be bookable that day.
    if (isGroupAppointment) {
      const existingKeys = new Set(dates.map((date) => getDateKey(date)));
      barberOffDates.forEach((date) => {
        if (!existingKeys.has(getDateKey(date))) dates.push(date);
      });
    }

    return dates;
  }, [availabilityMap, isGroupAppointment, barberOffDates]);

  if (calendarVisible) {
    return (
      <Animated.View
        exiting={calendarExitingAnimation}
        entering={calendarEnteringAnimation}
      >
        <Calendar
          monthsBehind={monthsBehind}
          monthsAhead={calendarMonthsAhead}
          selectedDate={selectedDate}
          currentMonth={calendarCurrentMonth}
          onDateSelect={handleCalendarDateSelect}
          onMonthChange={onMonthChange}
          testID={`${testID}-month`}
          minDate={minDate}
          maxDate={maxDate}
          disabledDates={disabledDates}
          waitlistOnlyDates={waitlistOnlyDates}
        />
      </Animated.View>
    );
  }

  const renderDay = ({ item: date }: { item: Date }) => {
    const dateKey = getDateKey(date);
    const availabilityCount = availabilityMap.get(dateKey) ?? 0;
    const isSelected = isSameDay(date, selectedDate);
    const isDisabled = isDateDisabled(date);
    const isWaitlistOnlyDay = isWaitlistOnly(date);
    const hasAvailability = availabilityCount > 0;

    return (
      <ChooseTimeCalendarDay
        date={date}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isWaitlistOnly={isWaitlistOnlyDay}
        hasAvailability={hasAvailability}
        availabilityCount={availabilityCount}
        onPress={() => onDateSelect(date)}
        testID={`${testID}-day-${dateKey}`}
      />
    );
  };

  return (
    <AnimatedView entering={FadeIn.duration(150)}>
      <VStack>
        <View style={styles.daysRow}>
          {weekDays.map((day) => (
            <React.Fragment key={day.toString()}>
              {renderDay({ item: day })}
            </React.Fragment>
          ))}

          <Box style={styles.openCalendarButtonContainer}>
            <Button
              size="small"
              variant="secondary"
              onPress={handleShowCalendar}
              style={styles.openCalendarButton}
              testID={`${testID}-open-calendar-button`}
            >
              <ChevronLeftIcon />
            </Button>
          </Box>
        </View>
      </VStack>
    </AnimatedView>
  );
}
